// Turns the cut fractions barGeometry returns into the two custom properties
// the CSS reads, plus the label's slant-aware padding.
//
// Percentages, not pixels. Both views share this because both must: the week
// view works in pixels and the month grid in percentage points, and a ratio is
// the only form that is correct in both. It is also the only form that survives
// a window resize — nothing in this package repaints on one.

/**
 * @param {HTMLElement} node  a .calendar-bar
 * @param {{cutLeft: number, cutRight: number}} geo  fractions of the bar's width
 */
export function applyCuts(node, geo) {
  const cutL = geo.cutLeft * 100;
  const cutR = geo.cutRight * 100;

  node.style.setProperty("--cut-l", `${cutL}%`);
  node.style.setProperty("--cut-r", `${cutR}%`);

  // The label sits on the bar's midline, where the diagonal has travelled
  // exactly half its width — so half the cut is the geometrically exact inset.
  // Only true while the bar is one line high; a wrapping bar would need the
  // full cut.
  //
  // These drive the title's `left`/`right`, NOT padding. Percentage padding
  // resolves against the containing block's width rather than the element's
  // own, so on a narrow bar it silently exceeded the bar and crushed the label
  // to nothing. Percentage offsets on an absolutely positioned child resolve
  // against its containing block — this bar — which is what we actually mean.
  node.style.setProperty("--pad-l", `calc(${cutL / 2}% + 8px)`);
  node.style.setProperty("--pad-r", `calc(${cutR / 2}% + 8px)`);
}

// Roughly how wide the label can be, in the caller's unit: the bar's box less
// the slant, halved because a one-line label sits on the midline where the
// diagonal has travelled half its width.
export function usableLabelWidth(geo) {
  return geo.width - ((geo.cutLeft + geo.cutRight) * geo.width) / 2;
}

// "B. Cruz" -> "BC". A one-day bar at the fortnight default leaves ~33px of
// solid core against ~40px of text, so the name truncated to "B. Cr" — which
// reads as a rendering fault rather than a bar that is simply too short. The
// full name stays in the title attribute either way.
export function initialsOf(title) {
  return String(title)
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word[0])
    .filter((c) => /[a-z]/i.test(c))
    .join("")
    .toUpperCase();
}

/**
 * The label a bar can actually seat.
 * @param {string} title
 * @param {{width: number, cutLeft: number, cutRight: number}} geo
 * @param {number} minWidth  in the SAME unit as geo.width
 */
export function barLabel(title, geo, minWidth) {
  const usable = usableLabelWidth(geo);
  if (usable >= minWidth) return { text: title, abbreviated: false };

  const initials = initialsOf(title);
  // If even the initials will not fit, showing them still beats a truncated
  // name: two letters read as an abbreviation, "B. Cr" reads as a bug.
  return { text: initials || title, abbreviated: true };
}
